import { API_BASE_URL } from "../constants";
import { Framework } from "./frameworkAdminService";

function authHeaders(token?: string): HeadersInit {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  return headers;
}

async function jsonOrThrow<T>(response: Response, action: string): Promise<T> {
  if (!response.ok) {
    let detail = "";
    try {
      const body = await response.json();
      detail =
        typeof body?.message === "string"
          ? `: ${body.message}`
          : Array.isArray(body?.message)
            ? `: ${body.message.join(", ")}`
            : "";
    } catch {
      /* body wasn't JSON */
    }
    throw new Error(`${action} failed: ${response.status}${detail}`);
  }
  if (response.status === 204) return undefined as T;
  return response.json();
}

export interface RubricSummary {
  id: string;
  name: string;
  frameworkName: string;
  criteriaCount: number;
  allowedScenarioCount: number;
  canEdit: boolean;
}

export interface RubricPayload {
  name: string;
  frameworkId: string;
  allowedScenarios: string[];
  conversationInstructions?: string;
  solutionInstructions?: string;
  /** Admin-only. Omitted entirely by non-admins and on create. */
  isGlobal?: boolean;
}

// The full rubric returned by GET /rubrics/:id.
export interface RubricDetail extends RubricPayload {
  id: string;
  framework: Framework;
  criteria: RubricCriterionRead[];
  isGlobal: boolean;
}

export async function listRubrics(
  token?: string,
  eligibleFor?: string,
): Promise<RubricSummary[]> {
  const qs = eligibleFor ? `?eligibleFor=${encodeURIComponent(eligibleFor)}` : "";
  const res = await fetch(`${API_BASE_URL}/rubrics${qs}`, { headers: authHeaders(token) });
  return jsonOrThrow(res, "List rubrics");
}

export async function getRubric(id: string, token?: string): Promise<RubricDetail> {
  const res = await fetch(`${API_BASE_URL}/rubrics/${id}`, { headers: authHeaders(token) });
  return jsonOrThrow(res, "Get rubric");
}

export async function createRubric(
  payload: RubricPayload,
  token?: string,
): Promise<{ id: string }> {
  const res = await fetch(`${API_BASE_URL}/rubrics`, {
    method: "POST",
    headers: authHeaders(token),
    body: JSON.stringify(payload),
  });
  return jsonOrThrow(res, "Create rubric");
}

export async function updateRubric(
  id: string,
  payload: RubricPayload,
  token?: string,
): Promise<{ id: string }> {
  const res = await fetch(`${API_BASE_URL}/rubrics/${id}`, {
    method: "PATCH",
    headers: authHeaders(token),
    body: JSON.stringify(payload),
  });
  return jsonOrThrow(res, "Update rubric");
}

export async function deleteRubric(id: string, token?: string): Promise<void> {
  const res = await fetch(`${API_BASE_URL}/rubrics/${id}`, {
    method: "DELETE",
    headers: authHeaders(token),
  });
  return jsonOrThrow(res, "Delete rubric");
}

export async function setScenarioRubric(
  scenarioId: string,
  rubricId: string | null,
  token?: string,
): Promise<{ id: string }> {
  const res = await fetch(`${API_BASE_URL}/scenarios/${scenarioId}/rubric`, {
    method: "PATCH",
    headers: authHeaders(token),
    body: JSON.stringify({ rubricId }),
  });
  return jsonOrThrow(res, "Set scenario rubric");
}

// ------------------------------------------------------------------
// Criteria editor types (write/input shape — examples nested under levels)
// ------------------------------------------------------------------

export interface ExampleSolutionInput {
  answer?: string;
  artifacts?: string[]; // GCS object keys
}
export interface ExampleInput {
  id?: string;
  conversationText?: string;
  solution?: ExampleSolutionInput;
}
export interface LevelInput {
  id?: string;
  name: string;
  minGrade: number;
  maxGrade: number;
  description: string;
  instructions?: string;
  examples: ExampleInput[];
}
export interface CriterionInput {
  id?: string;
  kind: "conversation" | "solution";
  name: string;
  description: string;
  instructions: string;
  weight: number;
  performanceLevels: LevelInput[];
}

// ------------------------------------------------------------------
// Criteria read types (exactly what GET /rubrics/:id returns — examples are a
// flat sibling array on the criterion, each tagged with performanceLevelId)
// ------------------------------------------------------------------

export interface RubricLevelRead {
  id: string;
  name: string;
  minGrade: number;
  maxGrade: number;
  description: string;
  instructions: string | null;
}
export interface RubricExampleRead {
  id: string;
  performanceLevelId: string;
  conversationText: string | null;
  solution: { id: string; answer: string | null; artifacts: string[] | null } | null;
}
export interface RubricCriterionRead {
  id: string;
  kind: "conversation" | "solution";
  name: string;
  description: string;
  instructions: string;
  weight: number;
  performanceLevels: RubricLevelRead[];
  examples: RubricExampleRead[];
}

export async function saveCriteria(
  rubricId: string,
  criteria: CriterionInput[],
  token?: string,
): Promise<{ id: string }> {
  const res = await fetch(`${API_BASE_URL}/rubrics/${rubricId}/criteria`, {
    method: "PUT",
    headers: authHeaders(token),
    body: JSON.stringify({ criteria }),
  });
  return jsonOrThrow(res, "Save criteria");
}

// ------------------------------------------------------------------
// Signed uploads (rubric example artifacts)
// ------------------------------------------------------------------

interface SignUploadResponse {
  objectKey: string;
  signedUrl: string;
  expiresAt: number;
}

export async function signRubricUpload(
  rubricId: string,
  file: File,
  token?: string,
): Promise<string> {
  const res = await fetch(`${API_BASE_URL}/rubrics/uploads/sign`, {
    method: "POST",
    headers: authHeaders(token),
    body: JSON.stringify({
      rubricId,
      filename: file.name,
      contentType: file.type || "application/octet-stream",
    }),
  });
  const { objectKey, signedUrl } = await jsonOrThrow<SignUploadResponse>(
    res,
    "Sign upload",
  );
  await uploadFileToGcs(signedUrl, file);
  return objectKey;
}

/**
 * PUT the file bytes to GCS using the signed URL. The server signs both
 * `Content-Type` and `x-goog-content-length-range` into the URL (see
 * GcsService.signWriteUrl), so the client MUST send both with values that
 * exactly match the signature. The length-range value here must stay in sync
 * with MAX_UPLOAD_BYTES in server/src/scenarios/gcs.service.ts.
 */
async function uploadFileToGcs(signedUrl: string, file: File): Promise<void> {
  const put = await fetch(signedUrl, {
    method: "PUT",
    headers: {
      "Content-Type": file.type || "application/octet-stream",
      "x-goog-content-length-range": "0,10485760",
    },
    body: file,
  });
  if (!put.ok) throw new Error(`Upload failed: ${put.status}`);
}
